import { HttpErrorResponse } from '@angular/common/http';
import { IDropdownResponse } from '../models/dynamic.model';
import {
  AddressCandidate,
  AddressVerificationRejection,
  AddressVerifyRequest,
} from '../models/address-verification.model';
import {
  ADDRESS_VERIFICATION_REJECTION_CODE,
  LookupDisplaySeparator,
  SMARTY_US_COUNTRY_CODES,
  StatusCodes,
} from './constants';
import { parseLookupCode } from './address-location-map.util';

export type VerifiedAddressField =
  | 'address_1'
  | 'address_2'
  | 'city'
  | 'state'
  | 'zipCode';

/** Raw address control values (lookup fields may hold "CODE - Name"). */
export interface AddressFormValues {
  address_1?: string | null;
  address_2?: string | null;
  state?: string | null;
  zipCode?: string | null;
  countryCode?: string | null;
}

const US_ZIP_BASE_LENGTH = 5;

function trimValue(value: string | null | undefined): string {
  return typeof value === 'string' ? value.trim() : '';
}

function normalizeForCompare(value: string | null | undefined): string {
  return trimValue(value)
    .toUpperCase()
    .replace(/[.,#]/g, '')
    .replace(/\s+/g, ' ');
}

/** Build the /verify body: codes for country and state, plain city name. */
export function buildVerifyRequest(
  values: AddressFormValues,
  city: string
): AddressVerifyRequest {
  return {
    address_1: trimValue(values.address_1),
    address_2: trimValue(values.address_2),
    city: trimValue(city),
    state: parseLookupCode(values.state),
    zipCode: trimValue(values.zipCode),
    countryCode: parseLookupCode(values.countryCode),
  };
}

export function isSmartyUsCountry(countryCode: string | null | undefined): boolean {
  return SMARTY_US_COUNTRY_CODES.includes(trimValue(countryCode).toUpperCase());
}

function isZipChanged(
  entered: string,
  suggested: string,
  isUs: boolean
): boolean {
  const a = normalizeForCompare(entered).replace(/\s/g, '');
  const b = normalizeForCompare(suggested).replace(/\s/g, '');
  if (isUs) {
    // Smarty appending ZIP+4 is not a correction.
    return a.slice(0, US_ZIP_BASE_LENGTH) !== b.slice(0, US_ZIP_BASE_LENGTH);
  }
  return a !== b;
}

/** Fields where the candidate differs from what the user entered. */
export function getChangedFields(
  request: AddressVerifyRequest,
  candidate: AddressCandidate
): Set<VerifiedAddressField> {
  const changed = new Set<VerifiedAddressField>();
  if (normalizeForCompare(request.address_1) !== normalizeForCompare(candidate.address_1)) {
    changed.add('address_1');
  }
  if (
    candidate.address_2 !== null &&
    candidate.address_2 !== undefined &&
    normalizeForCompare(request.address_2) !== normalizeForCompare(candidate.address_2)
  ) {
    changed.add('address_2');
  }
  if (normalizeForCompare(request.city) !== normalizeForCompare(candidate.city)) {
    changed.add('city');
  }
  if (
    candidate.state &&
    normalizeForCompare(request.state) !== normalizeForCompare(candidate.state)
  ) {
    changed.add('state');
  }
  if (
    candidate.zipCode &&
    isZipChanged(request.zipCode, candidate.zipCode, isSmartyUsCountry(request.countryCode))
  ) {
    changed.add('zipCode');
  }
  return changed;
}

export function formatStateDisplay(state: IDropdownResponse): string {
  return `${state.stateCode}${LookupDisplaySeparator}${state.name}`;
}

/** Match Smarty state values against master data: by code first, then by full name. */
export function matchState(
  states: IDropdownResponse[],
  values: (string | null | undefined)[]
): IDropdownResponse | undefined {
  const wanted = values
    .map(value => normalizeForCompare(value))
    .filter(value => !!value);
  if (!states.length || !wanted.length) {
    return undefined;
  }
  for (const value of wanted) {
    const byCode = states.find(
      state => normalizeForCompare(state.stateCode) === value
    );
    if (byCode) {
      return byCode;
    }
  }
  for (const value of wanted) {
    const byName = states.find(state => normalizeForCompare(state.name) === value);
    if (byName) {
      return byName;
    }
  }
  return undefined;
}

/** Form values to patch when the user picks a candidate (guide section 7). */
export function buildCandidatePatch(
  candidate: AddressCandidate,
  states: IDropdownResponse[]
): Partial<Record<VerifiedAddressField, string>> {
  const patch: Partial<Record<VerifiedAddressField, string>> = {};
  if (candidate.address_1) {
    patch.address_1 = candidate.address_1;
  }
  if (candidate.address_2 !== null && candidate.address_2 !== undefined) {
    patch.address_2 = candidate.address_2;
  }
  if (candidate.city) {
    patch.city = candidate.city;
  }
  if (candidate.zipCode) {
    patch.zipCode = candidate.zipCode;
  }
  const state = candidate.state ? matchState(states, [candidate.state]) : undefined;
  if (state) {
    patch.state = formatStateDisplay(state);
  }
  return patch;
}

/** Parties named in a save rejection (400 BUSINESS_RULE_VIOLATED with details.verification). */
export function getAddressVerificationRejections(
  error: unknown
): AddressVerificationRejection[] {
  if (
    !(error instanceof HttpErrorResponse) ||
    error.status !== StatusCodes.BadRequest ||
    error.error?.code !== ADDRESS_VERIFICATION_REJECTION_CODE
  ) {
    return [];
  }
  const verification = error.error?.details?.verification;
  return Array.isArray(verification) ? verification : [];
}

export function isAddressVerificationRejection(error: HttpErrorResponse): boolean {
  return getAddressVerificationRejections(error).length > 0;
}

/** Bring a field into view and focus it, e.g. after the user chooses to edit an address. */
export function revealAndFocusField(element: HTMLElement | null | undefined): void {
  if (!element) {
    return;
  }
  element.scrollIntoView({ behavior: 'smooth', block: 'center' });
  element.focus({ preventScroll: true });
}

export function getHttpErrorMessage(error: unknown): string {
  return error instanceof HttpErrorResponse ? error.error?.message ?? '' : '';
}
